import React, { useId } from 'react';
import { OptionGroup } from './OptionGroup';
import { MANUAL_IU_MAX, MANUAL_IU_MIN, OTHER_DOSE, productById } from '../data/products';
import { describeDoseRule } from '../utils/rekovelleDose';
import { formatDose } from '../utils/treatmentModel';
import type { DoseResult, PatientPreset } from '../types/simulation';
import type { ProductId } from '../types/treatment';
type DoseStepProps = {
  productId: ProductId;
  patient: PatientPreset;
  rekovelleDose: DoseResult;
  selection: string | null;
  manualValue: string;
  manualError: string | null;
  onSelect: (value: string) => void;
  onManualChange: (value: string) => void;
  actions: React.ReactNode;
};
export function DoseStep({
  productId,
  patient,
  rekovelleDose,
  selection,
  manualValue,
  manualError,
  onSelect,
  onManualChange,
  actions
}: DoseStepProps) {
  const product = productById(productId);
  const inputId = useId();
  const isRekovelle = productId === 'rekovelle';
  return <div className="flex min-h-[420px] flex-col" data-id="element-97" data-mp="1ey7qnxodnm1t">
      <p className="text-sm text-graphite" data-id="element-98" data-mp="1ewwlpuj74894">
        {product.name} · {product.molecule}
      </p>
      <h2 className="mt-2 max-w-3xl font-serif text-4xl font-normal tracking-[-0.01em] text-ink md:text-5xl" data-id="element-99" data-mp="19yv3tcxiaez8">
        What starting dose would you normally use for this patient?
      </h2>

      {isRekovelle ? <div className="mt-10 max-w-3xl border-y border-rule py-6" data-id="element-100" data-mp="1jpzg24vsrlqd">
          <p className="text-xs text-graphite" data-id="element-101" data-mp="w0oh8sy0c1ev">Labelled individualized starting dose</p>
          <p className="mt-1 font-serif text-[52px] font-light leading-none tracking-[-0.03em] text-ink tabular-nums sm:text-[72px] md:text-[96px]" data-id="element-102" data-mp="r1bue874yuca">
            {rekovelleDose.dose.toFixed(2)}
            <span className="ml-3 font-sans text-2xl tracking-normal text-graphite" data-id="element-103" data-mp="10yggk80zept3">μg / day</span>
          </p>
          <p className="mt-4 font-mono text-xs text-graphite" data-id="element-104" data-mp="2gefaofdrklac">{describeDoseRule(rekovelleDose, patient.weight)}</p>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-graphite" data-id="element-105" data-mp="2cdbufrrt0n8l">
            With REKOVELLE the starting dose isn't chosen freely — it is calculated from this patient's AMH and body
            weight.
          </p>
        </div> : <div className="mt-10" data-id="element-106" data-mp="1j0lw80d5sj9k">
          <OptionGroup label={`${product.name} starting dose`} columns="compact" value={selection} onChange={onSelect} options={[...product.options.map(o => ({
        value: String(o),
        label: formatDose(o, product.unit)
      })), {
        value: OTHER_DOSE,
        label: 'Other',
        hint: 'Enter dose'
      }]} data-id="element-107" data-mp="1j2gh5eofkbdn" />
          {selection === OTHER_DOSE && <div className="mt-6 max-w-xs" data-id="element-108" data-mp="zbbhaol8m2ci">
              <label htmlFor={inputId} className="text-xs text-graphite" data-id="element-109" data-mp="1444p5lc05qst">
                Daily starting dose
              </label>
              <div className={`mt-1.5 flex items-center rounded-xl border bg-white px-4 focus-within:border-accent focus-within:shadow-[0_0_0_1px_#2448c4] ${manualError ? 'border-over' : 'border-rule'}`} data-id="element-110" data-mp="7lq7tmopn3ft">
                <input id={inputId} type="number" inputMode="decimal" min={MANUAL_IU_MIN} max={MANUAL_IU_MAX} step={12.5} autoFocus value={manualValue} onChange={e => onManualChange(e.target.value)} placeholder="e.g. 175" aria-invalid={manualError !== null} aria-describedby={manualError ? `${inputId}-err` : undefined} className="w-full bg-transparent py-3 font-serif text-2xl text-ink tabular-nums outline-none placeholder:text-graphite/40" data-id="element-111" data-mp="3vbf1gjbln6t0" />
                <span className="text-sm text-graphite" data-id="element-112" data-mp="2yj1p9rmpdnr9">IU</span>
              </div>
              {manualError && <p id={`${inputId}-err`} className="mt-1.5 text-xs text-over" data-id="element-113" data-mp="2tlibstxtxf0d">
                  {manualError}
                </p>}
            </div>}
        </div>}

      <div className="mt-auto flex items-center justify-end gap-4 pt-10" data-id="element-114" data-mp="12fn9esu758dn">{actions}</div>
    </div>;
}